import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { useForm, type UseFormRegisterReturn } from 'react-hook-form';
import {
  type CroDto,
  type TrialDto,
  UpdateTrialRequestSchema,
  type UpdateTrialRequest,
} from '@ctms/contracts';
import { isHTTPError } from 'ky';
import { useState, type JSX } from 'react';
import { z } from 'zod';

import { getApiErrorMessage } from '../../../api/client.js';
import { trialsApi } from '../trials.api.js';
import { DICTIONARY_LIST_QUERY, trialQueryKeys } from '../trials.query-keys.js';
import { CriteriaCatalog } from '../criteria-catalog/criteria-catalog.js';
import { RoleCatalog } from '../role-catalog/role-catalog.js';
import { TeamCatalog } from '../team-catalog/team-catalog.js';
import { VisitCatalog } from '../visit-catalog/visit-catalog.js';
import { TrialKitsCatalog } from '../../kits/trial-kits-catalog/trial-kits-catalog.js';

export interface TrialCardProps {
  seed: TrialDto;
  canWrite: boolean;
  canWritePersonnel: boolean;
  onBack: () => void;
}

const fieldClass =
  'mt-1.5 w-full rounded-md border border-slate-700 bg-slate-950 px-3 py-2 text-sm text-slate-100 placeholder-slate-500 focus:border-cyan-500 focus:outline-none disabled:opacity-50 read-only:text-slate-300';

const labelClass = 'block text-xs font-medium uppercase tracking-wider text-slate-300';

const ISO_DATE = z.iso.date();

const TrialCardFormSchema = z
  .object({
    protocolNumber: z.string().trim().min(1, 'Numer protokołu jest wymagany').max(100),
    shortTitle: z.string().trim().min(1, 'Krótki tytuł jest wymagany').max(255),
    shortcut: z
      .string()
      .trim()
      .min(1, 'Skrót jest wymagany')
      // #7 (Piotr odp.5): ≤10 chars wherever the shortcut is CONFIGURED.
      .max(10, 'Skrót może mieć maksymalnie 10 znaków'),
    siteNumber: z.string().trim().min(1, 'Numer ośrodka w badaniu jest wymagany').max(50),
    croId: z.string(),
    comment: z.string().max(10_000),
    sivDate: z.string().refine(
      (value) => value === '' || ISO_DATE.safeParse(value).success,
      'Data inicjacji (SIV) ma format RRRR-MM-DD',
    ),
    covDate: z.string().refine(
      (value) => value === '' || ISO_DATE.safeParse(value).success,
      'Data zamknięcia (COV) ma format RRRR-MM-DD',
    ),
    active: z.boolean(),
    randomizationNumber: z.boolean(),
    labModule: z.boolean(),
    compliance: z.boolean(),
    travelCosts: z.boolean(),
    travelCostsInfo: z.string().max(10_000),
    vendorsInfo: z.string().max(10_000),
    ctaKind: z.enum(['', '2', '3']),
    paymentTimelines: z.string().refine(
      (value) => isPositiveDayCount(value, true),
      'Termin płatności podaj jako liczbę dni',
    ),
    paymentFrequency: z.string().refine(
      (value) => isPositiveDayCount(value, false),
      'Cykl płatności podaj jako liczbę dni',
    ),
    invoiceSendDescription: z.string().max(10_000),
    withholdingRate: z.string().refine(
      isWithholdingRate,
      'Stawka potrąceń pozostaje w zakresie od 0 do 100',
    ),
    feeVariants: z.boolean(),
    procedureVariants: z.boolean(),
  });

type TrialCardFormInput = z.infer<typeof TrialCardFormSchema>;

function isPositiveDayCount(value: string, allowEmpty: boolean): boolean {
  const trimmed = value.trim();
  if (trimmed.length === 0) {
    return allowEmpty;
  }
  if (!/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = Number(trimmed);
  return Number.isSafeInteger(parsed) && parsed > 0;
}

function isWithholdingRate(value: string): boolean {
  const trimmed = value.trim();
  if (!/^\d+$/.test(trimmed)) {
    return false;
  }
  const parsed = Number(trimmed);
  return parsed >= 0 && parsed <= 100;
}

function emptyToNull(value: string): string | null {
  const trimmed = value.trim();
  return trimmed.length === 0 ? null : trimmed;
}

function ctaKindToForm(kind: TrialDto['ctaKind']): TrialCardFormInput['ctaKind'] {
  if (kind === 2) {
    return '2';
  }
  if (kind === 3) {
    return '3';
  }
  return '';
}

function ctaKindToRequest(value: TrialCardFormInput['ctaKind']): 2 | 3 | null {
  if (value === '2') {
    return 2;
  }
  if (value === '3') {
    return 3;
  }
  return null;
}

function toFormValues(trial: TrialDto): TrialCardFormInput {
  return {
    protocolNumber: trial.protocolNumber,
    shortTitle: trial.shortTitle,
    shortcut: trial.shortcut,
    siteNumber: trial.siteNumber,
    croId: trial.croId == null ? '' : String(trial.croId),
    comment: trial.comment ?? '',
    sivDate: trial.sivDate ?? '',
    covDate: trial.covDate ?? '',
    active: trial.active,
    randomizationNumber: trial.randomizationNumber,
    labModule: trial.labModule,
    compliance: trial.compliance,
    travelCosts: trial.travelCosts,
    travelCostsInfo: trial.travelCostsInfo ?? '',
    vendorsInfo: trial.vendorsInfo ?? '',
    ctaKind: ctaKindToForm(trial.ctaKind),
    paymentTimelines: trial.paymentTimelines == null ? '' : String(trial.paymentTimelines),
    paymentFrequency: String(trial.paymentFrequency),
    invoiceSendDescription: trial.invoiceSendDescription ?? '',
    withholdingRate: String(trial.withholdingRate),
    feeVariants: trial.feeVariants,
    procedureVariants: trial.procedureVariants,
  };
}

function toUpdateTrialRequest(data: TrialCardFormInput): UpdateTrialRequest {
  const timelines = data.paymentTimelines.trim();
  return UpdateTrialRequestSchema.parse({
    protocolNumber: data.protocolNumber.trim(),
    shortTitle: data.shortTitle.trim(),
    shortcut: data.shortcut.trim(),
    siteNumber: data.siteNumber.trim(),
    croId: data.croId === '' ? null : Number(data.croId),
    comment: emptyToNull(data.comment),
    randomizationNumber: data.randomizationNumber,
    sivDate: data.sivDate === '' ? null : data.sivDate,
    covDate: data.covDate === '' ? null : data.covDate,
    active: data.active,
    travelCosts: data.travelCosts,
    travelCostsInfo: emptyToNull(data.travelCostsInfo),
    labModule: data.labModule,
    compliance: data.compliance,
    vendorsInfo: emptyToNull(data.vendorsInfo),
    ctaKind: ctaKindToRequest(data.ctaKind),
    paymentTimelines: timelines === '' ? null : Number(timelines),
    paymentFrequency: Number(data.paymentFrequency.trim()),
    invoiceSendDescription: emptyToNull(data.invoiceSendDescription),
    withholdingRate: Number(data.withholdingRate.trim()),
    feeVariants: data.feeVariants,
    procedureVariants: data.procedureVariants,
  });
}

function queryRetry(count: number, err: unknown): boolean {
  return isHTTPError(err) ? false : count < 3;
}

function croLabel(trial: TrialDto, cros: CroDto[], dictionaryReady: boolean): string {
  if (trial.croId == null) {
    return '— brak —';
  }
  const name = cros.find((cro) => cro.id === trial.croId)?.name;
  if (name) {
    return name;
  }
  return dictionaryReady ? '— brak —' : '…';
}

export function TrialCard({ seed, canWrite, canWritePersonnel, onBack }: TrialCardProps): JSX.Element {
  const trialQuery = useQuery({
    queryKey: trialQueryKeys.detail(seed.id),
    queryFn: () => trialsApi.getTrial(seed.id),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
  const sponsorsQuery = useQuery({
    queryKey: trialQueryKeys.sponsors.list(DICTIONARY_LIST_QUERY),
    queryFn: () => trialsApi.listSponsors(DICTIONARY_LIST_QUERY),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });
  const crosQuery = useQuery({
    queryKey: trialQueryKeys.cros.list(DICTIONARY_LIST_QUERY),
    queryFn: () => trialsApi.listCros(DICTIONARY_LIST_QUERY),
    staleTime: 5 * 60 * 1000,
    retry: queryRetry,
  });

  const loaded = trialQuery.data;
  const trial = loaded ?? seed;
  const sponsors = sponsorsQuery.data?.items ?? [];
  const cros = crosQuery.data?.items ?? [];
  const dictionariesReady = sponsorsQuery.isFetched && crosQuery.isFetched;
  const sponsorName =
    sponsors.find((sponsor) => sponsor.id === trial.sponsorId)?.name ??
    (sponsorsQuery.isFetched ? '—' : '…');

  return (
    <section aria-label={`Przestrzeń badania ${trial.shortcut}`} className="space-y-4">
      <div className="flex items-start justify-between gap-4 border-b border-slate-800 pb-4">
        <div role="region" aria-label="Nagłówek badania">
          <h2 className="text-lg font-semibold text-slate-100">Przestrzeń badania {trial.shortcut}</h2>
          <p className="mt-1 text-sm text-slate-200">
            {trial.shortTitle} · {trial.protocolNumber}
          </p>
          <p className="mt-1 text-xs text-slate-400">
            Sponsor: {sponsorName} · CRO: {croLabel(trial, cros, crosQuery.isFetched)} · Numer ośrodka: {trial.siteNumber}
          </p>
          <p className="mt-1 text-xs text-slate-300">{trial.active ? 'Aktywne' : 'Nieaktywne'}</p>
        </div>
        <button
          type="button"
          onClick={onBack}
          className="rounded-md border border-slate-700 px-3 py-1.5 text-xs text-slate-300"
        >
          Wróć do katalogu
        </button>
      </div>

      {trialQuery.isError && (
        <div role="alert" className="rounded-md border border-rose-800/60 bg-rose-950/40 p-3 text-sm text-rose-300">
          Nie udało się wczytać karty badania.
          <button type="button" onClick={() => void trialQuery.refetch()} className="ml-3 underline">
            Spróbuj ponownie
          </button>
        </div>
      )}

      <div className="grid grid-cols-2 items-start gap-4">
        {loaded && dictionariesReady && (
          <TrialCardForm
            trial={loaded}
            sponsorName={sponsors.find((sponsor) => sponsor.id === loaded.sponsorId)?.name ?? '—'}
            cros={cros}
            canWrite={canWrite}
          />
        )}
        <VisitCatalog trialId={seed.id} canWrite={canWrite} />
      </div>
      <div className="grid grid-cols-2 items-start gap-4">
        <RoleCatalog trialId={seed.id} canWritePersonnel={canWritePersonnel} />
        <TeamCatalog trialId={seed.id} sivDate={trial.sivDate} canWritePersonnel={canWritePersonnel} />
      </div>
      <CriteriaCatalog trialId={seed.id} canWrite={canWrite} />
      <TrialKitsCatalog trialId={seed.id} canWrite={canWrite} />
    </section>
  );
}

function TrialCardForm({
  trial,
  sponsorName,
  cros,
  canWrite,
}: {
  trial: TrialDto;
  sponsorName: string;
  cros: CroDto[];
  canWrite: boolean;
}): JSX.Element {
  const queryClient = useQueryClient();
  const [errorMessage, setErrorMessage] = useState<string | null>(null);
  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<TrialCardFormInput>({
    resolver: zodResolver(TrialCardFormSchema),
    defaultValues: toFormValues(trial),
  });

  const save = useMutation({
    mutationFn: (request: UpdateTrialRequest) => trialsApi.updateTrial(trial.id, request),
    onSuccess: (updated) => {
      setErrorMessage(null);
      queryClient.setQueryData(trialQueryKeys.detail(trial.id), updated);
      void queryClient.invalidateQueries({ queryKey: trialQueryKeys.lists() });
    },
    onError: (err: unknown) => {
      void getApiErrorMessage(err, 'Nie udało się zapisać karty.').then(setErrorMessage);
    },
  });

  const onSubmit = (data: TrialCardFormInput): void => {
    if (!canWrite) {
      return;
    }
    setErrorMessage(null);
    save.mutate(toUpdateTrialRequest(data));
  };

  const locked = !canWrite || save.isPending;

  return (
    <section aria-label="Karta badania" className="rounded-lg border border-slate-800 bg-slate-900/60 p-4">
      <h3 className="text-sm font-semibold text-slate-100">Karta badania</h3>
      <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4" noValidate>
        {errorMessage && (
          <p role="alert" className="text-sm text-rose-300">
            {errorMessage}
          </p>
        )}
        {save.isSuccess && (
          <p role="status" className="text-sm text-emerald-300">
            Karta została zapisana
          </p>
        )}

        <div className="grid grid-cols-2 gap-4">
          <TextField id="trial-card-protocol" label="Numer protokołu" error={errors.protocolNumber?.message} registration={register('protocolNumber')} disabled={locked} />
          <TextField id="trial-card-title" label="Krótki tytuł" error={errors.shortTitle?.message} registration={register('shortTitle')} disabled={locked} />
          <TextField id="trial-card-shortcut" label="Skrót" error={errors.shortcut?.message} registration={register('shortcut')} disabled={locked} />
          <TextField id="trial-card-site" label="Numer ośrodka w badaniu" error={errors.siteNumber?.message} registration={register('siteNumber')} disabled={locked} />
          <div>
            <label htmlFor="trial-card-sponsor" className={labelClass}>Sponsor</label>
            <input id="trial-card-sponsor" value={sponsorName} readOnly className={fieldClass} />
          </div>
          <div>
            <label htmlFor="trial-card-cro" className={labelClass}>CRO</label>
            <select id="trial-card-cro" disabled={locked} className={fieldClass} {...register('croId')}>
              <option value="">— brak —</option>
              {cros.map((cro) => (
                <option key={cro.id} value={String(cro.id)}>
                  {cro.name}
                </option>
              ))}
            </select>
          </div>
        </div>

        <TextField id="trial-card-comment" label="Komentarz" error={errors.comment?.message} registration={register('comment')} disabled={locked} />

        <div className="grid grid-cols-2 gap-4">
          <TextField id="trial-card-siv" label="Data inicjacji (SIV)" type="date" error={errors.sivDate?.message} registration={register('sivDate')} disabled={locked} />
          <TextField id="trial-card-cov" label="Data zamknięcia (COV)" type="date" error={errors.covDate?.message} registration={register('covDate')} disabled={locked} />
        </div>

        <CheckField label="Badanie aktywne" registration={register('active')} disabled={locked} />

        <div className="space-y-3 border-t border-slate-800 pt-4">
          <h4 className="text-xs font-semibold uppercase tracking-wider text-slate-400">Ustawienia numeracji i modułów</h4>
          <CheckField label="Numer randomizacyjny" registration={register('randomizationNumber')} disabled={locked} />
          <CheckField label="Moduł laboratoryjny" registration={register('labModule')} disabled={locked} />
          <CheckField label="Obliczanie compliance (badania tabletkowe)" registration={register('compliance')} disabled={locked} />
          <CheckField label="Rozliczanie kosztów dojazdów pacjentów" registration={register('travelCosts')} disabled={locked} />
          <TextField id="trial-card-travel" label="Opis dojazdów" error={errors.travelCostsInfo?.message} registration={register('travelCostsInfo')} disabled={locked} />
          <TextField id="trial-card-vendors" label="Systemy zewnętrzne" error={errors.vendorsInfo?.message} registration={register('vendorsInfo')} disabled={locked} />
          <div>
            <label htmlFor="trial-card-cta" className={labelClass}>Rodzaj umowy</label>
            <select id="trial-card-cta" disabled={locked} className={fieldClass} {...register('ctaKind')}>
              <option value="">—</option>
              <option value="2">dwustronna</option>
              <option value="3">trójstronna</option>
            </select>
          </div>
          <div className="grid grid-cols-2 gap-4">
            <TextField id="trial-card-timelines" label="Termin płatności (dni)" error={errors.paymentTimelines?.message} registration={register('paymentTimelines')} disabled={locked} />
            <TextField id="trial-card-frequency" label="Cykl płatności (dni)" error={errors.paymentFrequency?.message} registration={register('paymentFrequency')} disabled={locked} />
          </div>
          <TextField id="trial-card-invoice" label="Opis wysyłki faktury" error={errors.invoiceSendDescription?.message} registration={register('invoiceSendDescription')} disabled={locked} />
          <TextField id="trial-card-withholding" label="Stawka potrąceń (%)" error={errors.withholdingRate?.message} registration={register('withholdingRate')} disabled={locked} />
          <CheckField label="Anekstowanie kwot wizyt" registration={register('feeVariants')} disabled={locked} />
          <CheckField label="Anekstowanie kwot procedur dodatkowych" registration={register('procedureVariants')} disabled={locked} />
        </div>

        {!canWrite && (
          <p className="text-xs text-amber-300">Nie masz uprawnienia do zapisu badań.</p>
        )}

        <div className="flex justify-end border-t border-slate-800 pt-4">
          <button
            type="submit"
            disabled={locked}
            className="rounded-md bg-cyan-600 px-3.5 py-1.5 text-xs font-medium text-white hover:bg-cyan-500 disabled:cursor-not-allowed disabled:opacity-50"
          >
            {save.isPending ? 'Zapisywanie…' : 'Zapisz kartę'}
          </button>
        </div>
      </form>
    </section>
  );
}

function TextField({
  id,
  label,
  error,
  registration,
  disabled,
  type = 'text',
}: {
  id: string;
  label: string;
  error?: string;
  registration: UseFormRegisterReturn;
  disabled: boolean;
  type?: 'text' | 'date';
}): JSX.Element {
  return (
    <div>
      <label htmlFor={id} className={labelClass}>{label}</label>
      <input
        id={id}
        type={type}
        disabled={disabled}
        className={type === 'date' ? `${fieldClass} scheme-dark cursor-pointer` : fieldClass}
        {...registration}
        onClick={(event) => {
          if (type !== 'date') {
            return;
          }
          event.preventDefault();
          const input = event.currentTarget;
          if (input.disabled || typeof input.showPicker !== 'function') {
            return;
          }
          try {
            input.showPicker();
          } catch {
            input.focus();
          }
        }}
        onKeyDown={(event) => {
          if (type === 'date' && event.key !== 'Tab' && event.key !== 'Escape') {
            event.preventDefault();
          }
        }}
      />
      {error && <p className="mt-1 text-xs text-rose-400">{error}</p>}
    </div>
  );
}

function CheckField({
  label,
  registration,
  disabled,
}: {
  label: string;
  registration: UseFormRegisterReturn;
  disabled: boolean;
}): JSX.Element {
  return (
    <label className="flex items-center gap-2 text-sm text-slate-200">
      <input type="checkbox" disabled={disabled} {...registration} />
      {label}
    </label>
  );
}
